/**
 * Search Executor
 * Main search execution logic with client-side filtering and sorting
 */

import { getIndex } from './client.js';
import { state, saveToLocalStorage, updateFacets } from '../core/state.js';
import { buildFilters } from '../filters/filter-builder.js';
import { getGeoSearchParams } from '../filters/geo-params.js';
import { dateYmdToInt, getCampStartDate } from '../utils/date-utils.js';
import { mergeFacetKeys } from '../utils/facet-utils.js';
import { getAvailableFacets } from './facets-fetcher.js';
import { hasActiveFilters, checkFiltersMatch } from './dual-search.js';
import { showFilterMismatchBanner } from '../ui/banner-manager.js';
import { isEmbeddedMode } from '../modes/embedded.js';

// These will be injected by index.js or are imported in later stages
let renderResults = null;
let updateURL = null;
let updateFilterUI = null;
let updateFiltersCount = null;
let toggleSkeleton = null;
let runDecisionFlow = null;
let showCorrectedLocationBannerIfNeeded = null;
let applyPresentation = null;
let hideFallbackBanner = null;
let computeShowDistance = null;
let showError = null;

/**
 * Set UI dependencies (called from index.js after UI modules loaded)
 */
export function setUIDependencies(deps) {
  renderResults = deps.renderResults;
  updateURL = deps.updateURL;
  updateFilterUI = deps.updateFilterUI;
  updateFiltersCount = deps.updateFiltersCount;
  toggleSkeleton = deps.toggleSkeleton;
  runDecisionFlow = deps.runDecisionFlow;
  showCorrectedLocationBannerIfNeeded = deps.showCorrectedLocationBannerIfNeeded;
  applyPresentation = deps.applyPresentation;
  hideFallbackBanner = deps.hideFallbackBanner;
  computeShowDistance = deps.computeShowDistance;
  showError = deps.showError;
}

// First performSearch() on this page vs later runs (e.g. filter changes via performSearchAuto)
let hasCompletedMainSearch = false;

// Brand-specific UI/state overrides before each Algolia query (monolith runExceptions;
// isFirstSearch = first performSearch on this page)
function runExceptions({ isFirstSearch } = {}) {
  if (
    isFirstSearch &&
    state.brand.length > 0 &&
    state.brand.every((b) =>
      ['us baseball academy', 'euro sports camps', 'chelsea fc'].includes(
        b.toLowerCase()
      )
    )
  ) {
    state.live = false;
  }
}

/**
 * Main search function
 * Executes Algolia search with filters, applies client-side date filtering,
 * handles sorting, facets, and renders results
 */
export function performSearch() {
  const isFirstSearch = !hasCompletedMainSearch;
  hasCompletedMainSearch = true;

  console.log('🔍 [ALGOLIA REQUEST] performSearch - main search request');
  state.page = 1;
  state.displayedHits = state.hitsPerPage;

  runExceptions({ isFirstSearch });

  saveToLocalStorage();

  const filters = buildFilters();
  if (updateFiltersCount) updateFiltersCount();
  if (toggleSkeleton) toggleSkeleton(true);

  // Add geo search if coordinates available
  const geoParams = getGeoSearchParams();

  // NOTE: campSearchCenter is persisted in renderResults (the single final render of
  // the search), NOT here — this query may be superseded by a decision-flow fallback
  // step with a different/absent center, and writing here would race/wipe that.

  const searchParams = {
    filters: filters || undefined,
    hitsPerPage: 1000, // Get up to 1000 results per query, but use nbHits for total count
    page: 0, // Always get first page
    facets: ['sportSlug', 'feeCodes', 'gender', 'brands', 'skills', 'live'],
    // Don't use numericFilters - we'll filter on client to include camps without dates
    getRankingInfo: true,
    clickAnalytics: true,
    ...geoParams,
  };

  console.log('🔍 [ALGOLIA REQUEST] Search params:', {
    aroundLatLng: searchParams.aroundLatLng,
    aroundRadius: searchParams.aroundRadius,
    aroundRadiusMiles: searchParams.aroundRadius
      ? searchParams.aroundRadius / 1609
      : null,
    filters: searchParams.filters,
    coordinates: state.coordinates,
    zip: state.zip,
  });

  // Add sorting - distance handled by Algolia geo; other sorts handled client-side
  if (state.sort === 'title-asc') {
    searchParams.sortFacetValuesBy = 'alpha';
  }

  const index = getIndex();
  index
    .search('', searchParams)
    .then(({ queryID, hits, nbHits, facets }) => {
      localStorage.setItem('algoliaQueryID', queryID);
      // If primary search returned 0, clear previous results and run fallback logic
      if (!nbHits || nbHits === 0) {
        state.isInitialSearch = false;
        state.allFilteredHits = [];
        state.totalHits = 0;
        if (renderResults) renderResults([], 0, false, null, queryID);
        if (toggleSkeleton) toggleSkeleton(false);
        // Don't run fallback when user has active sidebar filters (skills/types/brands/gender)
        // — 0 results is correct when the user's filter combination has no matches in this area
        const hasSidebarFilters =
          state.skills.length > 0 || state.types.length > 0 ||
          state.brand.length > 0 || (state.brandExclude && state.brandExclude.length > 0) ||
          state.filterCoed || state.filterMaleOnly || state.filterFemaleOnly;
        // In embedded mode, don't run decision flow (no fallback searches needed)
        if (!isEmbeddedMode() && !hasSidebarFilters && runDecisionFlow) return runDecisionFlow();
        // Update UI when sidebar filters block fallback (updateFilterUI is normally called inside getAvailableFacets.then)
        if (updateFilterUI) updateFilterUI();
        if (updateFiltersCount) updateFiltersCount();
        if (updateURL) updateURL();
        return;
      }

      let filteredHits = hits || [];
      let filteredTotal = nbHits || 0;

      // Apply client-side date filtering using sessionStartDays (YYYYMMDD integers)
      // Camps without sessionStartDays are always included (no date data)
      if (state.beginDates && state.endDates) {
        const startDay = dateYmdToInt(state.beginDates);
        const endDay = dateYmdToInt(state.endDates);
        if (startDay && endDay) {
          console.log('📅 Date filtering:', {
            beginDates: state.beginDates,
            endDates: state.endDates,
            startDay,
            endDay,
            totalHits: filteredHits.length,
          });

          filteredHits = filteredHits.filter((camp) => {
            // Include camps without sessionStartDays (no date info)
            if (!camp.sessionStartDays?.length) return true;
            // Include camps where any session starts within the selected range
            return camp.sessionStartDays.some((d) => d >= startDay && d <= endDay);
          });

          console.log('📅 After date filtering:', {
            filteredHits: filteredHits.length,
            originalHits: hits.length,
          });

          filteredTotal = filteredHits.length;
        }
      }

      // If filtered results are 0 after date filtering, clear and use fallback logic
      if (filteredHits.length === 0 && state.beginDates && state.endDates) {
        state.isInitialSearch = false;
        console.log('🔄 No results after date filtering, using fallback logic');
        state.allFilteredHits = [];
        state.totalHits = 0;
        if (renderResults) renderResults([], 0, false, null, queryID);
        if (toggleSkeleton) toggleSkeleton(false);
        // Don't run fallback when user has active sidebar filters (skills/types/brands/gender)
        // — 0 results is correct when the user's filter combination has no matches in this area
        const hasSidebarFiltersDate =
          state.skills.length > 0 || state.types.length > 0 ||
          state.brand.length > 0 || (state.brandExclude && state.brandExclude.length > 0) ||
          state.filterCoed || state.filterMaleOnly || state.filterFemaleOnly;
        if (!isEmbeddedMode() && !hasSidebarFiltersDate && runDecisionFlow) return runDecisionFlow();
        // Update UI when sidebar filters block fallback (updateFilterUI is normally called inside getAvailableFacets.then)
        if (updateFilterUI) updateFilterUI();
        if (updateFiltersCount) updateFiltersCount();
        if (updateURL) updateURL();
        return;
      }

      // Embedded mode only: on the very first search with live preset the fetch was
      // done without the live filter — check client-side whether any live camps exist
      // and apply or auto-fallback. Full mode (/search/results) always sends live:true
      // to Algolia (monolith parity), so no client-side pass is needed there.
      if (state.isInitialSearch && state.live === true && isEmbeddedMode()) {
        const liveHits = filteredHits.filter((h) => h.live === true);
        if (liveHits.length > 0) {
          console.log(`✅ [LIVE FILTER] Initial search: ${liveHits.length} live camps found, filtering to live only`);
          filteredHits = liveHits;
          filteredTotal = liveHits.length;
        } else {
          console.log('⚠️ [LIVE FILTER] Initial search: no live camps found, falling back to all camps');
          state.live = null; // uncheck live filter (updateFilterUI will reflect this)
        }
      }
      state.isInitialSearch = false;

      // Show banners for corrected/unrecognized locations
      if (state.showCorrectedLocationBanner) {
        if (showCorrectedLocationBannerIfNeeded)
          showCorrectedLocationBannerIfNeeded(filteredTotal);
      } else if (
        state.showUnrecognizedLocationBanner &&
        state.unrecognizedLocationBannerContent
      ) {
        if (applyPresentation) {
          applyPresentation(
            {
              useDistance:
                state.unrecognizedLocationBannerContent.useDistance || false,
              headerHtml: state.unrecognizedLocationBannerContent.headerHtml,
              subtextHtml: state.unrecognizedLocationBannerContent.subtextHtml,
            },
            filteredTotal
          );
        }
      } else if (state.geocodeResult?.isCampFound && state.foundCampHit) {
        // Dual search mode: re-evaluate mismatch banner on every filter change
        const mismatch = hasActiveFilters() && !checkFiltersMatch(state.foundCampHit);
        showFilterMismatchBanner(mismatch);
        if (computeShowDistance)
          state.showDistance = computeShowDistance(true, null);
      } else {
        if (hideFallbackBanner) hideFallbackBanner();
        if (computeShowDistance)
          state.showDistance = computeShowDistance(true, null);
      }

      let updatedFacets = facets || {};
      if (!updatedFacets.brands) {
        updatedFacets.brands = {};
      }

      // Compute skills from the date-filtered hits client-side.
      // Algolia's getAvailableFacets() query doesn't know about client-side date filtering,
      // so it would return skills from the full geo/sport/live set (e.g. 35 hits) rather
      // than the actually visible subset (e.g. 1 hit after date filter). This causes phantom
      // skill options that yield 0 results when clicked.
      const clientSkillsFacets = {};
      filteredHits.forEach((hit) => {
        if (hit.skills && Array.isArray(hit.skills)) {
          hit.skills.forEach((s) => {
            clientSkillsFacets[s] = (clientSkillsFacets[s] || 0) + 1;
          });
        }
      });

      // Fetch full brands, feeCodes, and skills lists in a single query (merge to preserve key order)
      getAvailableFacets().then(
        ({ brands: brandFacets, feeCodes: typesFacets, skills: skillsFacets }) => {
          state.allFacets.brands = mergeFacetKeys(
            state.allFacets.brands || {},
            brandFacets || {}
          );
          state.allFacets.feeCodes = mergeFacetKeys(
            state.allFacets.feeCodes || {},
            typesFacets || {}
          );
          // Always merge the full list (like brands/feeCodes) — replacing the baseline
          // with the date-filtered subset collapsed the sidebar group to the checked
          // options only. Date-aware counts go to state.facets.skills below instead.
          state.allFacets.skills = mergeFacetKeys(
            state.allFacets.skills || {},
            skillsFacets || {}
          );

          // Sort results if needed
          if (state.sort === 'title-desc') {
            filteredHits.sort((a, b) => {
              const titleA = (a.title || '').toLowerCase();
              const titleB = (b.title || '').toLowerCase();
              return titleB.localeCompare(titleA);
            });
          } else if (state.sort === 'title-asc') {
            filteredHits.sort((a, b) => {
              const titleA = (a.title || '').toLowerCase();
              const titleB = (b.title || '').toLowerCase();
              return titleA.localeCompare(titleB);
            });
          } else if (state.sort === 'date-asc') {
            filteredHits.sort((a, b) => {
              const dateA = getCampStartDate(a);
              const dateB = getCampStartDate(b);
              if (!dateA && !dateB) {
                const titleA = (a.title || '').toLowerCase();
                const titleB = (b.title || '').toLowerCase();
                return titleA.localeCompare(titleB);
              }
              if (!dateA) return 1;
              if (!dateB) return -1;
              const dateDiff = dateA - dateB;
              if (dateDiff === 0) {
                const titleA = (a.title || '').toLowerCase();
                const titleB = (b.title || '').toLowerCase();
                return titleA.localeCompare(titleB);
              }
              return dateDiff;
            });
          }

          // Exclude found camp from results if we're in dual search mode
          // This prevents the same camp appearing in both Section 1 and main results
          if (state.foundCampObjectID) {
            const beforeCount = filteredHits.length;
            filteredHits = filteredHits.filter(
              (hit) => hit.objectID !== state.foundCampObjectID
            );
            if (filteredHits.length < beforeCount) {
              console.log(
                '🔍 [DUAL SEARCH] Excluded found camp from results:',
                state.foundCampObjectID
              );
              filteredTotal = Math.max(0, filteredTotal - 1);
            }
          }

          // Featured camps A/B test: pin featured camps to the top (variant B; no-op otherwise).
          // Gated on the active sport filter — city-only (no sport) does not pin.
          filteredHits =
            window.CampsUtils?.applyFeaturedBoost?.(
              filteredHits,
              state.sportSlug,
            ) || filteredHits;

          // Store all filtered results for Load More functionality
          state.allFilteredHits = filteredHits;
          state.totalHits = filteredTotal;

          // Reset displayed count on new search
          if (state.page === 1) {
            state.displayedHits = state.hitsPerPage;
          }

          // Get hits to display (for Load More)
          const hitsToDisplay = filteredHits.slice(0, state.displayedHits);

          // Update facets for dynamic filtering (map feeCodes back to types for UI)
          if (updatedFacets.feeCodes) {
            updatedFacets.types = updatedFacets.feeCodes;
          }
          updateFacets(updatedFacets);
          // Algolia facets can't see the client-side date filter — when dates are
          // active, counts come from the actually visible (date-filtered) hits.
          if (state.beginDates && state.endDates) {
            state.facets.skills = clientSkillsFacets;
          }

          // Render results with Load More button
          if (renderResults)
            renderResults(hitsToDisplay, filteredTotal, false, null, queryID);

          // Track view of search results or empty results
          if (window.AlgoliaAnalyticsHandlers) {
            if (filteredTotal === 0) {
              window.AlgoliaAnalyticsHandlers.trackEmptyResults(queryID);
            } else {
              const objectIDs = hitsToDisplay
                .map((hit) => hit.objectID || hit.id)
                .filter(Boolean)
                .map(String);
              if (objectIDs.length > 0) {
                window.AlgoliaAnalyticsHandlers.trackView(objectIDs, queryID);
              }
            }
          }

          // Update URL
          if (updateURL) updateURL();

          // Update filter UI
          if (updateFilterUI) updateFilterUI();
          if (updateFiltersCount) updateFiltersCount();

          if (toggleSkeleton) toggleSkeleton(false);
        }
      );
    })
    .catch((error) => {
      console.error('Search error:', error);
      if (showError) showError();
      if (toggleSkeleton) toggleSkeleton(false);
    });
}

console.log('📦 Search executor module loaded');
